/**
 * React Query hooks over the /admin/* surface.
 *
 * Conventions inherited from `useMe.ts`: an exported query-key constant per
 * resource so a mutation elsewhere can invalidate it without re-deriving the
 * key, and `retry: false` wherever a failure is an ANSWER rather than a
 * hiccup. A 403 from an admin route is not transient — the server has ruled on
 * the caller's role, and retrying it three times just delays showing the
 * operator the truth.
 *
 * STALE TIMES ARE CHOSEN BY WHAT THE NUMBER IS FOR, not by taste:
 *   - ops alerts: 30s. It is a detector read. An operator watching an incident
 *     wants it to move; one left open on a second monitor must not sit on a
 *     green report from ten minutes ago.
 *   - params: 0, and ALWAYS refetched on mount. This is the optimistic-
 *     concurrency surface. A cached `version` is a stale token, and composing a
 *     write against one earns a 409 that reads to the operator as a bug in the
 *     console rather than as another operator having saved first.
 *   - users / reports / audit: 60s. List reads, no write token in them.
 */
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  adminOpsApi,
  adminParamsApi,
  adminUsersApi,
  adminReportsApi,
  adminMarketsApi,
  adminQuarantineApi,
  adminMatchCreditApi,
  adminReferralsApi,
  adminResettlementApi,
  adminBetsApi,
  type AdminUsersQuery,
} from '@/api/adminEndpoints';
import type {
  AdminAdjustMatchCreditBody,
  AdminApplyQuarantineBody,
  AdminClearQuarantineBody,
  AdminParams,
  AdminRejectResettlementBody,
  AdminResettleBody,
  AdminResolveReferralBody,
  AdminSetKycBody,
  AdminSetRoleBody,
  AdminSetStatusBody,
  AdminSettleBetBody,
  AdminUnlockSavingsBody,
  BetResettlementStatus,
} from '@/types/api';

export const ADMIN_OPS_ALERTS_KEY = ['admin', 'ops-alerts'] as const;
export const ADMIN_COVERAGE_KEY = ['admin', 'settlement-coverage'] as const;
export const ADMIN_PARAMS_KEY = ['admin', 'params'] as const;
export const ADMIN_AUDIT_KEY = ['admin', 'audit-log'] as const;
export const ADMIN_USERS_KEY = ['admin', 'users'] as const;
export const ADMIN_REPORTS_KEY = ['admin', 'reports'] as const;
export const ADMIN_VISIBILITY_KEY = ['admin', 'markets-visibility'] as const;
export const ADMIN_RESETTLEMENT_KEY = ['admin', 'bet-resettlement-requests'] as const;
export const ADMIN_QUARANTINE_KEY = ['admin', 'fixture-quarantines'] as const;
export const ADMIN_ODDS_PROVIDERS_KEY = ['admin', 'odds-engine', 'providers'] as const;
export const ADMIN_ODDS_HEALTH_KEY = ['admin', 'odds-engine', 'health'] as const;
export const ADMIN_ODDS_CACHE_KEY = ['admin', 'odds-engine', 'cache'] as const;
export const ADMIN_ALIAS_GAPS_KEY = ['admin', 'odds-engine', 'alias-gaps'] as const;

/**
 * Every standing detector, evaluated server-side on demand.
 *
 * `refetchInterval` is deliberately NOT set by default. A console left open on
 * a spare monitor polling every 30s for a week is a load pattern nobody chose;
 * the page that wants live behaviour opts in explicitly.
 */
export function useOpsAlerts(options: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: ADMIN_OPS_ALERTS_KEY,
    queryFn: adminOpsApi.getAlerts,
    staleTime: 30_000,
    retry: false,
    ...(options.refetchInterval !== undefined
      ? { refetchInterval: options.refetchInterval }
      : {}),
  });
}

export function useSettlementCoverage() {
  return useQuery({
    queryKey: ADMIN_COVERAGE_KEY,
    queryFn: adminOpsApi.getSettlementCoverage,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * What each adapter DECLARES it covers. Registry configuration, not a probe —
 * it changes on deploy and on nothing else, so 60s is generous rather than
 * tight.
 */
export function useOddsProviders() {
  return useQuery({
    queryKey: ADMIN_ODDS_PROVIDERS_KEY,
    queryFn: adminOpsApi.getOddsProviders,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * The LIVE per-adapter probe, and the one read on this screen that can change
 * between two glances — so 15s, shorter than every other admin list.
 *
 * An operator opens this while a provider is flapping; a minute-old `ok: true`
 * beside a red ops alert is the report arguing with the detector.
 */
export function useOddsHealth() {
  return useQuery({
    queryKey: ADMIN_ODDS_HEALTH_KEY,
    queryFn: adminOpsApi.getOddsHealth,
    staleTime: 15_000,
    retry: false,
  });
}

/** TTL cache counters for the quote and result fan-outs. */
export function useOddsCache() {
  return useQuery({
    queryKey: ADMIN_ODDS_CACHE_KEY,
    queryFn: adminOpsApi.getOddsCache,
    staleTime: 15_000,
    retry: false,
  });
}

/** Provider identity disagreements plus the alias table's own self-check. */
export function useAliasGaps() {
  return useQuery({
    queryKey: ADMIN_ALIAS_GAPS_KEY,
    queryFn: adminOpsApi.getAliasGaps,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * Drop both TTL caches. WRITE-gated.
 *
 * The 204 carries NO BODY, so there is no post-write snapshot to seed the cache
 * with — the counters are invalidated and re-read instead. Health is
 * invalidated with them because the next fan-out re-probes every provider, so
 * the health numbers on screen stop describing the engine the moment this
 * returns.
 *
 * Deliberately NOT invalidated: the audit log. The contract declares no reason
 * field on this endpoint, so there is nothing for the server to have written
 * there, and invalidating it would imply otherwise.
 */
export function useInvalidateOddsCache() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: adminOpsApi.invalidateOddsCache,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ADMIN_ODDS_CACHE_KEY });
      void qc.invalidateQueries({ queryKey: ADMIN_ODDS_HEALTH_KEY });
    },
  });
}

/**
 * The single config row, including its `version`.
 *
 * `staleTime: 0` + `refetchOnMount: 'always'` is not caution, it is the
 * concurrency contract: the version travelling back on the next PUT must be
 * one this console actually read, not one cached before a colleague's write.
 */
export function useAdminParams() {
  return useQuery({
    queryKey: ADMIN_PARAMS_KEY,
    queryFn: adminParamsApi.get,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });
}

/**
 * Write the config row.
 *
 * On success the response IS the new snapshot, so it is written straight into
 * the cache — re-fetching would leave a window where the form re-renders from
 * the pre-write row and looks like the save was ignored.
 *
 * A 409 `stale_params_version` is deliberately NOT swallowed here. It carries
 * the current snapshot in `error.details`, and only the calling screen knows
 * how to present "someone else saved while you were editing" against the
 * fields the operator has open.
 */
export function useUpdateAdminParams() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<AdminParams> & { expectedVersion: number; note?: string }) =>
      adminParamsApi.update(body),
    onSuccess: (updated) => {
      qc.setQueryData(ADMIN_PARAMS_KEY, updated);
      // The audit log gained a row; anything showing it is now a row behind.
      void qc.invalidateQueries({ queryKey: ADMIN_AUDIT_KEY });
    },
  });
}

export function useAdminAuditLog(params: { limit?: number; cursor?: string } = {}) {
  return useQuery({
    queryKey: [...ADMIN_AUDIT_KEY, params] as const,
    queryFn: () => adminParamsApi.auditLog(params),
    staleTime: 60_000,
    retry: false,
  });
}

export function useAdminUsers(params: AdminUsersQuery = {}) {
  return useQuery({
    queryKey: [...ADMIN_USERS_KEY, params] as const,
    queryFn: () => adminUsersApi.list(params),
    staleTime: 60_000,
    retry: false,
  });
}

/** `userId` may be undefined while a route param is resolving; the query idles. */
export function useAdminUserDetail(userId: string | undefined) {
  return useQuery({
    queryKey: [...ADMIN_USERS_KEY, 'detail', userId] as const,
    queryFn: () => adminUsersApi.detail(userId!),
    enabled: !!userId,
    staleTime: 30_000,
    retry: false,
  });
}

/*
  ---------------------------------------------------------------------------
  Per-user mutations
  ---------------------------------------------------------------------------

  All four invalidate the SAME two things and nothing else: this user's detail
  row and the audit log. Invalidating rather than writing the response into the
  cache is deliberate here, unlike params above — these responses are a summary
  of the write (`{ userId, status, previousStatus, revokedSessions }`), not the
  detail row, so writing one into the detail key would replace a full record
  with a fragment. The list is left alone: a page of users is a position in one
  ordering, and refetching it under the operator would move rows.
*/
function useUserDetailKey(userId: string) {
  return [...ADMIN_USERS_KEY, 'detail', userId] as const;
}

function useAfterUserWrite(userId: string) {
  const qc = useQueryClient();
  const detailKey = useUserDetailKey(userId);
  return () => {
    void qc.invalidateQueries({ queryKey: detailKey });
    void qc.invalidateQueries({ queryKey: ADMIN_AUDIT_KEY });
  };
}

/** WRITE (owner|admin). 409 `would_leave_zero_owners`, 400 `no_change`. */
export function useSetUserRole(userId: string) {
  const after = useAfterUserWrite(userId);
  return useMutation({
    mutationFn: (body: AdminSetRoleBody) => adminUsersApi.setRole(userId, body),
    onSuccess: after,
  });
}

export function useSetUserKyc(userId: string) {
  const after = useAfterUserWrite(userId);
  return useMutation({
    mutationFn: (body: AdminSetKycBody) => adminUsersApi.setKyc(userId, body),
    onSuccess: after,
  });
}

export function useSetUserStatus(userId: string) {
  const after = useAfterUserWrite(userId);
  return useMutation({
    mutationFn: (body: AdminSetStatusBody) => adminUsersApi.setStatus(userId, body),
    onSuccess: after,
  });
}

/**
 * Release locked Savings.
 *
 * The idempotency key is supplied by the CALLER, not generated here, and that
 * is the whole safety property: a key minted inside `mutationFn` would be fresh
 * on every retry, which is exactly what makes a replay release a second tranche.
 * The screen mints one when the operator confirms and keeps it for the life of
 * that confirmation.
 */
export function useUnlockSavings(userId: string) {
  const after = useAfterUserWrite(userId);
  return useMutation({
    mutationFn: (args: { body: AdminUnlockSavingsBody; idempotencyKey: string }) =>
      adminUsersApi.unlockSavings(userId, args.body, args.idempotencyKey),
    onSuccess: after,
  });
}

/** Signed legacy-credit adjustment. 422 `insufficient_match_credit` on an over-debit. */
export function useAdjustMatchCredit(userId: string) {
  const after = useAfterUserWrite(userId);
  return useMutation({
    mutationFn: (body: AdminAdjustMatchCreditBody) => adminMatchCreditApi.adjust(body),
    onSuccess: after,
  });
}

/** Resolve a fraud-held referral attribution. 409 `not_held` if already resolved. */
export function useResolveReferral(userId: string) {
  const after = useAfterUserWrite(userId);
  return useMutation({
    mutationFn: (args: { attributionId: string; body: AdminResolveReferralBody }) =>
      adminReferralsApi.resolve(args.attributionId, args.body),
    onSuccess: after,
  });
}

/*
  ---------------------------------------------------------------------------
  Re-settlement — the correction queue
  ---------------------------------------------------------------------------

  `staleTime: 0` + `refetchOnMount: 'always'`, for the same reason as params
  above and not by caution: every row carries `currentSettlementVersion`, which
  the apply path sends back as `expectedSettlementVersion`. A cached row is a
  stale write token, and composing a correction against one earns a 409
  `version_mismatch` that reads to an operator as a bug in the console rather
  than as the bet having moved. The freshest possible row is the point.
*/
export function useResettlementRequests(
  params: { status?: BetResettlementStatus; limit?: number } = {},
) {
  return useQuery({
    queryKey: [...ADMIN_RESETTLEMENT_KEY, params] as const,
    queryFn: () => adminResettlementApi.list(params),
    staleTime: 0,
    refetchOnMount: 'always',
    retry: false,
  });
}

/**
 * Both writes invalidate the queue AND the audit log, and nothing else.
 *
 * The response is a summary of the write (`reversedEventCount`,
 * `settlementKind`), not a queue row, so writing it into the list cache would
 * replace a record with a fragment — the same reasoning as the per-user
 * mutations above.
 */
function useAfterResettlementWrite() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ADMIN_RESETTLEMENT_KEY });
    void qc.invalidateQueries({ queryKey: ADMIN_AUDIT_KEY });
  };
}

/** OWNER_ONLY. 409 `not_settled` | `outcome_unchanged` | `version_mismatch` | `unlock_provenance_unknown`. */
export function useResettleBet() {
  const after = useAfterResettlementWrite();
  return useMutation({
    mutationFn: (args: { betId: string; body: AdminResettleBody }) =>
      adminResettlementApi.resettle(args.betId, args.body),
    onSuccess: after,
  });
}

/** OWNER_ONLY. 409 `not_pending` if the proposal was already resolved. */
export function useRejectResettlement() {
  const after = useAfterResettlementWrite();
  return useMutation({
    mutationFn: (args: { id: string; body: AdminRejectResettlementBody }) =>
      adminResettlementApi.reject(args.id, args.body),
    onSuccess: after,
  });
}

/** Settle a PENDING bet (ANY_ADMIN). Nothing to reverse — idempotent on status. */
export function useSettleBet() {
  const after = useAfterResettlementWrite();
  return useMutation({
    mutationFn: (args: { betId: string; body: AdminSettleBetBody }) =>
      adminBetsApi.settle(args.betId, args.body),
    onSuccess: after,
  });
}

/*
  ---------------------------------------------------------------------------
  Fixture quarantines
  ---------------------------------------------------------------------------

  `staleTime: 0` + `refetchOnMount: 'always'`, and this one is not a write
  token — it is that the two writes are BOTH state transitions on a row whose
  current state decides which action is even offered. An `active` row can be
  cleared and cannot be re-applied; a `pending_refresh` row is the reverse. A
  cached row hands the operator the button the server will refuse, and the
  refusal (409) reads as a broken console rather than as someone else having
  acted first.

  30s `refetchInterval` while mounted: the whole reason `pending_refresh`
  exists is that it clears itself, without an operator doing anything, when the
  fixture's own catalogue transaction succeeds. A screen that never re-reads
  would show a fixture as withheld indefinitely after it was genuinely released.
*/
export function useFixtureQuarantines() {
  return useQuery({
    queryKey: ADMIN_QUARANTINE_KEY,
    queryFn: adminQuarantineApi.list,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: 30_000,
    retry: false,
  });
}

/**
 * Both writes invalidate the list AND the audit log, and nothing else.
 *
 * Neither response is written into the cache: the contract declares no
 * response content for either, so there is nothing to write, and the list read
 * is the authority on what state the row is now in anyway.
 */
function useAfterQuarantineWrite() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ADMIN_QUARANTINE_KEY });
    void qc.invalidateQueries({ queryKey: ADMIN_AUDIT_KEY });
  };
}

/** WRITE. 409 `fixture_already_quarantined` when one is already active. */
export function useApplyQuarantine() {
  const after = useAfterQuarantineWrite();
  return useMutation({
    mutationFn: (body: AdminApplyQuarantineBody) => adminQuarantineApi.apply(body),
    onSuccess: after,
  });
}

/**
 * WRITE. Requests a clear; the fixture stays unplaceable until its own
 * catalogue write releases it. 404 not held, 409 already pending.
 */
export function useClearQuarantine() {
  const after = useAfterQuarantineWrite();
  return useMutation({
    mutationFn: (args: { fixtureId: string; body: AdminClearQuarantineBody }) =>
      adminQuarantineApi.clear(args.fixtureId, args.body),
    onSuccess: after,
  });
}

export function useAdminReports() {
  return useQuery({
    queryKey: ADMIN_REPORTS_KEY,
    queryFn: adminReportsApi.summary,
    staleTime: 60_000,
    retry: false,
  });
}

export function useMarketVisibility() {
  return useQuery({
    queryKey: ADMIN_VISIBILITY_KEY,
    queryFn: adminMarketsApi.visibility,
    staleTime: 60_000,
    retry: false,
  });
}
